import {createContext, useContext} from 'react'

/**
 * Signals that the current React subtree is already wrapped in an anchor/link.
 * Child link components (GDILinkBlock, GDICtaBlock when configured as a link)
 * should read this and skip emitting their own anchor to avoid invalid nested
 * `<a>` elements in the DOM.
 *
 * Usage:
 *   <GDILinkContext.Provider value={true}>
 *       {children}
 *   </GDILinkContext.Provider>
 */
export const GDILinkContext = createContext(false)

export const useIsInsideGDILink = () => useContext(GDILinkContext)

/**
 * Signals that the current subtree should display its text/CTA layers only on
 * hover (the `DisplayTextAndCTAsOnHover` flag from GDICarouselGridBlock).
 * GDIContentTileBlock reads this and applies the hover-reveal CSS to itself
 * so the wrapper preserves any per-tile gridColumn/colspan.
 */
export const GDIHoverRevealContext = createContext(false)

export const useIsHoverReveal = () => useContext(GDIHoverRevealContext)

/**
 * Provides the parent grid's column count per breakpoint to descendant tiles.
 * Tiles use it to clamp their authored `ColSpan` so a span never exceeds the
 * available tracks (which would otherwise create zero-width implicit columns
 * and break the layout).
 *
 * Value shape: Chakra responsive object `{base, md, lg}` (numbers) or a scalar.
 */
export const GDIGridColumnsContext = createContext(null)

export const useGDIGridColumns = () => useContext(GDIGridColumnsContext)

/**
 * Carries the resolved per-breakpoint product-tile feature toggles authored on
 * GDICarouselGridBlock.ProductCarouselGridStylesPerViewType. GDIProductTileBlock
 * reads this and forwards the values to ProductTile, which applies them as
 * Chakra responsive `display` values on the corresponding subcomponents.
 *
 * Value shape (each field is a Chakra responsive object or undefined):
 *   {productName, productPrice, wishlistIcon, onImageBadge, productTags,
 *    colorSwatches, quickActionsDisplay, forceTileMode}
 *
 * `forceTileMode` is a responsive object of 'quick-view' | 'quick-add' that
 * overrides ProductTile's screensize-based mode picking when only one of the
 * QV/QA toggles is Yes for that breakpoint.
 */
export const GDIProductTogglesContext = createContext(null)

export const useGDIProductToggles = () => useContext(GDIProductTogglesContext)
